import { Schema as S } from "effect"
import type { AddonKey } from "@/domain/addon/organization-addon"
import { PLAN_REGISTRY } from "@/domain/plan/plan-registry"
import type { PlanKey } from "./plan-key"

/**
 * How `ChangeInput.targetAddonKeys` is interpreted by PlanChangeService:
 *
 * - "set" (default when targetAddonKeys is supplied): treat the list as the
 *   complete standalone-addon set after the change. Plan-bundled addons
 *   (PLAN_REGISTRY[targetPlanKey].includedAddonKeys) are inferred from the plan
 *   and excluded from the standalone list.
 * - "add": union the listed keys with the org's current standalone addons.
 * - "remove": subtract the listed keys from the org's current standalone addons.
 */
export const AddonChangeMode = S.Literal("set", "add", "remove")
export type AddonChangeMode = S.Schema.Type<typeof AddonChangeMode>

/**
 * Compute the final standalone-addon set after a change, applying mode
 * semantics (spec §6.1.3). Plan-bundled addons
 * (PLAN_REGISTRY[targetPlanKey].includedAddonKeys) are NOT stored as
 * standalone items — they are inferred from the plan and stripped here.
 *
 * Lives in the domain layer (not the service) so both PlanChangeService and the
 * in-transaction add-on reconciliation helper can import it without a cycle.
 */
export const computeTargetStandaloneAddons = (args: {
  readonly currentStandalone: ReadonlyArray<AddonKey>
  readonly targetPlanKey: PlanKey
  readonly request: {
    readonly targetAddonKeys?: ReadonlyArray<AddonKey>
    readonly addonChangeMode?: AddonChangeMode
  }
}): ReadonlyArray<AddonKey> => {
  const bundled = new Set(PLAN_REGISTRY[args.targetPlanKey].includedAddonKeys)
  const stripBundled = (xs: ReadonlyArray<AddonKey>) =>
    xs.filter((k) => !bundled.has(k))

  if (args.request.targetAddonKeys === undefined) {
    return stripBundled(args.currentStandalone)
  }

  const mode = args.request.addonChangeMode ?? "set"
  const requested = args.request.targetAddonKeys
  switch (mode) {
    case "set":
      return stripBundled(Array.from(new Set(requested)))
    case "add":
      return stripBundled(
        Array.from(new Set([...args.currentStandalone, ...requested]))
      )
    case "remove":
      return stripBundled(
        args.currentStandalone.filter((k) => !requested.includes(k))
      )
    default: {
      const _exhaustive: never = mode
      throw new Error(`unreachable addonChangeMode: ${String(_exhaustive)}`)
    }
  }
}
